import type { StartResponse } from "./types";

/**
 * Every call here is to our own backend. No vendor key is ever present in the
 * browser — the backend creates the conversation and hands back only a room URL.
 */

const BASE = import.meta.env.VITE_API_BASE ?? "";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    headers: { "Content-Type": "application/json" },
    ...init,
  });

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = await response.json();
      detail = body.detail ?? detail;
    } catch {
      /* non-JSON error body; the status text will do */
    }
    throw new Error(detail);
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

export function startConversation(context?: string): Promise<StartResponse> {
  return request<StartResponse>("/api/conversations", {
    method: "POST",
    body: JSON.stringify({ context: context ?? null }),
  });
}

export function endConversation(sessionId: string): Promise<void> {
  return request<void>(`/api/conversations/${sessionId}/end`, { method: "POST" });
}

export function websocketUrl(sessionId: string): string {
  if (BASE) {
    const url = new URL(BASE);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    url.pathname = `/ws/${sessionId}`;
    return url.toString();
  }
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}/ws/${sessionId}`;
}
